import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {changeQuestionModal, uploadSlideImage, uploadSlideVideo} from "../../../../app/slices/modals/questionEditModal";
import {ImageFill, TextParagraph, Upload} from "react-bootstrap-icons";
import {ADMIN_LANG} from "../../../../settings";
import React, {ChangeEvent, useState} from "react";
import RichTextField from "../../RichTextField";
import {Col, Row, Form, Button, InputGroup} from "react-bootstrap";

type Props = {
    language: string;
}

const QuestionEditSlide = ({language}: Props) => {
    const dispatch = useAppDispatch();
    const {slide_content, content_id, isUploading} = useAppSelector((state) => state.questionEditModal);
    const [image, setImage] = useState<File | null>(null);
    const [video, setVideo] = useState<File | null>(null);

    const handleSlideTypeChange = (type: string) => {
        dispatch(changeQuestionModal({
            slide_content: {
                ...slide_content,
                type,
            }
        }));
    }

    const handleContentChange = (content: string) => {
        const existingLanguages = slide_content?.languages || [];
        const languageIndex = existingLanguages.findIndex((lang) => lang.language_code === language);

        let updatedLanguages;

        if (languageIndex !== -1) {
            updatedLanguages = existingLanguages.map((lang) =>
                lang.language_code === language ? {...lang, content} : lang
            );
        } else {
            updatedLanguages = [
                ...existingLanguages,
                {
                    language_code: language,
                    content,
                },
            ];
        }

        dispatch(
            changeQuestionModal({
                slide_content: {
                    ...slide_content,
                    languages: updatedLanguages,
                },
            })
        );
    };

    const handleAutoplayChange = (autoplay: boolean) => {
        dispatch(changeQuestionModal({
            slide_content: {
                ...slide_content,
                video_autoplay: autoplay
            }
        }))
    }

    const handleLoopChange = (loop: boolean) => {
        dispatch(changeQuestionModal({
            slide_content: {
                ...slide_content,
                video_loop: loop
            }
        }))
    };

    const slideTypes = [
        {type: '1', imageFirst: true, vertical: false},
        {type: '2', imageFirst: false, vertical: false},
        // {type: '3', imageFirst: true, vertical: true},
        // {type: '4', imageFirst: false, vertical: true},
    ];

    const typeOptions = slideTypes.map(({type, imageFirst, vertical}) => {
        const isSelected = slide_content?.type === type;
        return (
            <div
                key={type}
                className={`slide-type ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSlideTypeChange(type)}
                style={{cursor: 'pointer'}}
            >
                <div className={`d-flex ${vertical ? 'flex-column align-items-center' : ''}`}>
                    {imageFirst ? <ImageFill size={40}/> : <TextParagraph size={40}/>}
                    {imageFirst ? (
                        <TextParagraph size={40} className={`${vertical ? 'mt-1' : 'ms-1'}`}/>
                    ) : (
                        <ImageFill size={40} className={`${vertical ? 'mt-1' : 'ms-1'}`}/>
                    )}
                </div>
            </div>
        );
    });

    const handleImageUpload = () => {
        if (!image || !content_id) return;
        let formData = new FormData();
        formData.append("file", image);
        formData.append("content_id", content_id);
        formData.append("language_code", language);
        dispatch(uploadSlideImage(formData));
    }

    const handleVideoUpload = () => {
        if (!video || !content_id) return;
        let formData = new FormData();
        formData.append("file", video);
        formData.append("content_id", content_id);
        formData.append("language_code", language);
        dispatch(uploadSlideVideo(formData));
    }


    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setImage(e.target.files[0]);
        }
    };

    const handleVideoChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setVideo(e.target.files[0]);
        }
    };

    const handleRemoveImage = () => {
        const updatedLanguages = slide_content.languages.map((lang) =>
            lang.language_code === language
                ? {...lang, image: undefined} // Correctly set image to undefined
                : lang // Return the unchanged language object if the condition is not met
        );

        dispatch(changeQuestionModal({
            slide_content: {
                ...slide_content,
                languages: updatedLanguages
            }
        }));
    }

    const handleRemoveVideo = () => {
        const updatedLanguages = slide_content.languages.map((lang) =>
            lang.language_code === language
                ? {...lang, video: undefined}
                : lang
        );

        dispatch(changeQuestionModal({
            slide_content: {
                ...slide_content,
                languages: updatedLanguages
            }
        }));
    }

    const contentValue = slide_content.languages.find((lang) => lang.language_code === language)?.content || '';
    const contentExists = slide_content.languages.find((lang) => lang.language_code === language);

    const slideImage = slide_content.languages.find((lang) => lang.language_code === language)?.image;
    const slideVideo = slide_content.languages.find((lang) => lang.language_code === language)?.video;

    return (
        <Col xs={12}>
            <Row className='gy-3'>
                <Col xs={12}>
                    <Form.Label>{ADMIN_LANG.slide_positioning}</Form.Label>
                    <div className='d-flex align-items-center'>
                        {typeOptions}
                    </div>
                </Col>

                <>
                    {slideImage && slideImage.thumbnail_url ? (
                        <Col xs={12}>
                            <Form.Group>
                                <Form.Label>{ADMIN_LANG.image}</Form.Label>
                                <div className='d-md-flex align-items-center'>
                                    <img width='250px' src={slideImage.thumbnail_url} alt='Slide preview'
                                         className='img-fluid rounded'/>
                                    <Button variant='secondary' onClick={handleRemoveImage}
                                            className='ms-md-3 mt-3 mt-md-0'>{ADMIN_LANG.change}</Button>
                                </div>
                            </Form.Group>
                        </Col>
                    ) : slideVideo && slideVideo.url ? (
                        <Col xs={12}>
                            <Form.Group controlId={`video_${language}`}>
                                <Form.Label>{ADMIN_LANG.video} ({language.toUpperCase()})</Form.Label>
                                <div className='d-md-flex align-items-center'>
                                    <video
                                        width='250px'
                                        src={slideVideo.url}
                                        controls
                                        className='img-fluid rounded'
                                    />
                                    <div className='ms-md-3 mt-3 mt-md-0'>
                                        <Form.Switch
                                            label={ADMIN_LANG.autoplay}
                                            id={`autoplay_${language}`}
                                            className='form-label'
                                            checked={slide_content.video_autoplay}
                                            onChange={(e) => handleAutoplayChange(e.target.checked)}
                                        /><Form.Switch
                                            label={ADMIN_LANG.loop_video}
                                            id={`loop_${language}`}
                                            className='form-label pb-3'
                                            checked={slide_content.video_loop}
                                            onChange={(e) => handleLoopChange(e.target.checked)}
                                        />
                                        <Button variant='secondary' onClick={handleRemoveVideo}>{ADMIN_LANG.change}</Button>
                                    </div>

                                </div>
                            </Form.Group>
                        </Col>
                    ) : (
                        <>
                            <Col xs={12}>
                                <Form.Group controlId={`image_${language}`}>
                                    <Form.Label>{ADMIN_LANG.image} ({language.toUpperCase()})</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            onChange={handleImageChange}
                                            type='file'
                                            accept='image/jpeg,image/png,image/jpg'
                                        />
                                        {image &&
                                        <Button
                                            variant='success'
                                            disabled={isUploading}
                                            onClick={handleImageUpload}
                                        >
                                        <Upload/> <span className='ms-2'>{ADMIN_LANG.upload_image}</span>
                                        </Button>
                                        }
                                    </InputGroup>
                                    <Form.Text className='help-text'
                                               dangerouslySetInnerHTML={{__html: ADMIN_LANG.slide_image_help}}></Form.Text>
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group controlId={`video_${language}`}>
                                    <Form.Label>{ADMIN_LANG.video} ({language.toUpperCase()})</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="file"
                                            accept="video/mp4"
                                            onChange={handleVideoChange}
                                        />
                                        {video &&
                                            <Button
                                            variant='success'
                                            disabled={isUploading}
                                            onClick={handleVideoUpload}
                                        >
                                        <Upload/> <span className='ms-2'>{ADMIN_LANG.upload_video}</span>
                                        </Button>
                                        }
                                    </InputGroup>
                                    <Form.Text className='help-text'
                                               dangerouslySetInnerHTML={{__html: ADMIN_LANG.slide_video_help}}></Form.Text>
                                </Form.Group>
                            </Col>
                        </>
                    )}
                </>

                <Col xs={12}>
                    <Form.Group controlId={`slide_content_${language}`}>
                        <Form.Label>{ADMIN_LANG.slide_content} ({language.toUpperCase()})</Form.Label>
                        {contentExists && (
                            <RichTextField
                                value={contentValue}
                                onChange={handleContentChange}
                            />
                        )}
                    </Form.Group>
                </Col>
            </Row>
        </Col>
    );
}

export default QuestionEditSlide;